import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import computeScoreColor from 'components/DriverAX/utils/bonusScoreColourUtils';
import Inputs from 'components/Inputs';
import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';

const CardWrapper = styled.div`
  background-color: var(--color-bg-muted);
  padding: 12px;
  border-radius: 8px;
`;

const ThresholdRow = ({
  value,
  thresholds,
  direction,
  onChange,
  onBlur,
  onRemove,
  removeTestId,
}: {
  value: number;
  thresholds: number[];
  direction: 'red-to-green' | 'green-to-red';
  onChange: (rawValue: string) => void;
  onBlur: () => void;
  onRemove: () => void;
  removeTestId: string;
}) => {
  const swatchColor = computeScoreColor({
    value,
    colourConfig: { mode: 'manual', thresholds, direction },
  });

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 8,
      }}
    >
      <div
        style={{
          width: 20,
          height: 20,
          borderRadius: 4,
          flexShrink: 0,
          backgroundColor: swatchColor ?? 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border)',
        }}
      />
      <Inputs.TextInput
        type="number"
        value={isNaN(value) ? '' : value.toString()}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        style={{ width: 80 }}
      />
      <Button
        size="X-Small"
        type="Ghost - No Padding"
        icon="cross"
        onClick={onRemove}
        testId={removeTestId}
      />
    </div>
  );
};

/**
 * Configures a red <-> green gradient across threshold limits, as rendered by
 * computeScoreColor. Used by dataset fields ('Colour Coded Number') and V5
 * matrix series.
 *
 * When isModeSelectable is false the config is always 'manual' - automatic
 * mode assumes a 0-100 score, which doesn't suit arbitrary metrics.
 */
const ThresholdColourInput = ({
  colourConfig,
  onChange,
  isModeSelectable,
}: {
  colourConfig?: FleetOps.BonusScoreColourConfig;
  onChange: (newConfig: FleetOps.BonusScoreColourConfig) => void;
  isModeSelectable?: boolean;
}) => {
  const mode = isModeSelectable ? colourConfig?.mode ?? 'automatic' : 'manual';
  const direction = colourConfig?.direction ?? 'red-to-green';

  const [thresholds, setThresholds] = useState<number[]>(
    colourConfig?.thresholds ?? [],
  );

  const colourModeOptions = useMemo((): DropdownOption[] => {
    const toOption = (
      label: string,
      newMode: FleetOps.BonusScoreColourConfig['mode'],
    ): DropdownOption => ({
      label,
      isSelected: mode === newMode,
      onSelected: () => {
        onChange({ ...(colourConfig ?? { mode: newMode }), mode: newMode });
      },
    });

    return [toOption('Automatic', 'automatic'), toOption('Manual', 'manual')];
  }, [colourConfig, mode, onChange]);

  const directionOptions = useMemo((): DropdownOption[] => {
    const toOption = (
      label: string,
      newDirection: 'red-to-green' | 'green-to-red',
    ): DropdownOption => ({
      label,
      isSelected: direction === newDirection,
      onSelected: () => {
        onChange({
          ...(colourConfig ?? { mode: 'manual' }),
          direction: newDirection,
        });
      },
    });

    return [
      toOption('Red to Green', 'red-to-green'),
      toOption('Green to Red', 'green-to-red'),
    ];
  }, [colourConfig, direction, onChange]);

  const updateThresholds = useCallback(
    (newThresholds: number[]) => {
      setThresholds(newThresholds);
      onChange({
        ...(colourConfig ?? { mode: 'manual' }),
        thresholds: newThresholds,
      });
    },
    [colourConfig, onChange],
  );

  const handleChange = useCallback(
    (index: number, rawValue: string) => {
      const parsed = parseFloat(rawValue);
      updateThresholds(thresholds.map((t, i) => (i === index ? parsed : t)));
    },
    [thresholds, updateThresholds],
  );

  // Sort on blur so the list reorders once the user finishes typing a value,
  // without jumping while they're mid-entry.
  const handleBlur = useCallback(() => {
    const sorted = thresholds.filter((t) => !isNaN(t)).sort((a, b) => a - b);
    updateThresholds(sorted);
  }, [thresholds, updateThresholds]);

  const addThreshold = useCallback(() => {
    const last = thresholds.length > 0 ? thresholds[thresholds.length - 1] : 0;
    updateThresholds([...thresholds, last + 10]);
  }, [thresholds, updateThresholds]);

  const removeThreshold = useCallback(
    (index: number) => {
      updateThresholds(thresholds.filter((_, i) => i !== index));
    },
    [thresholds, updateThresholds],
  );

  return (
    <CardWrapper>
      {isModeSelectable && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4 }}>
            <Typography.Body type="Label">Colour Mode</Typography.Body>
          </div>
          <Inputs.Dropdown
            options={colourModeOptions}
            selectedLabel={mode === 'automatic' ? 'Automatic' : 'Manual'}
          />
        </div>
      )}
      {mode === 'manual' && (
        <>
          <div style={{ marginBottom: 12 }}>
            <div style={{ marginBottom: 4 }}>
              <Typography.Body type="Label">Colour Direction</Typography.Body>
            </div>
            <Inputs.Dropdown
              options={directionOptions}
              selectedLabel={
                direction === 'red-to-green' ? 'Red to Green' : 'Green to Red'
              }
            />
          </div>
          <div>
            <div style={{ marginBottom: 8 }}>
              <Typography.Body type="Label">Thresholds</Typography.Body>
            </div>
            {thresholds.map((threshold, index) => (
              <ThresholdRow
                key={index}
                value={threshold}
                thresholds={thresholds}
                direction={direction}
                onChange={(v) => handleChange(index, v)}
                onBlur={handleBlur}
                onRemove={() => removeThreshold(index)}
                removeTestId={`remove-threshold-${index}`}
              />
            ))}
            <Button
              size="Small"
              type="Secondary"
              label="+ Add Threshold"
              onClick={addThreshold}
            />
          </div>
        </>
      )}
    </CardWrapper>
  );
};

export default ThresholdColourInput;
